import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {MutationObserver | null} */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#ensureWawaDropdownStyles();
    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    if (this.#state.activeItem) {
      const submenu = findSubmenu(this.#state.activeItem);
      if (submenu) this.#alignSubmenuToItem(this.#state.activeItem, submenu);
    }
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /** @type {State} */
  #state = { activeItem: null };

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);
    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) previouslyActiveItem.ariaExpanded = 'false';

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);
    if (!hasSubmenu && !isDefaultSlot) submenu = this.overflowMenu;

    if (submenu) {
      submenu.dataset.active = '';
      if (isDefaultSlot && hasSubmenu) this.#prepareWawaDropdown(item, submenu);

      this.#cleanupMutationObserver();
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (isDefaultSlot && hasSubmenu) this.#prepareWawaDropdown(item, submenu);
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });
      setTimeout(() => this.#cleanupMutationObserver(), 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }
    if (!submenu) finalHeight = 0;

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  deactivate(event) {
    if (!(event.target instanceof Element)) return;
    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);
    const isMovingToSubmenu = event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu = event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');
    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;
    setTimeout(() => this.#deactivate(), 450);
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    const submenu = findSubmenu(item);

    if (
      this.overflowListHovered ||
      this.overflowMenu?.matches(':hover') ||
      item.parentElement?.matches(':hover') ||
      submenu?.matches(':hover')
    ) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    if (submenu) delete submenu.dataset.active;
  };

  #prepareWawaDropdown(item, submenu) {
    const inner = submenu.querySelector('.menu-list__submenu-inner');
    const panel = submenu.querySelector('.mega-menu');
    const columns = submenu.querySelectorAll('.mega-menu__column');
    const column = columns.length === 1 ? columns[0] : null;

    if (!(inner instanceof HTMLElement) || !(panel instanceof HTMLElement) || !(column instanceof HTMLElement)) {
      this.#alignSubmenuToItem(item, submenu);
      return;
    }

    const hasNestedMenu = Boolean(column.querySelector('.mega-menu__link--parent'));
    const hasImages = Boolean(column.querySelector('.mega-menu__link-image'));
    const wrappers = Array.from(column.querySelectorAll(':scope > div')).filter(
      (wrapper) => wrapper instanceof HTMLElement && wrapper.querySelector(':scope > .mega-menu__link')
    );

    if (hasNestedMenu || hasImages || wrappers.length < 3 || wrappers.length > 12) {
      this.#alignSubmenuToItem(item, submenu);
      return;
    }

    inner.classList.add('wawa-dropdown');
    panel.classList.add('wawa-dropdown__panel');
    column.classList.add('wawa-dropdown__grid');

    wrappers.forEach((wrapper) => {
      if (!(wrapper instanceof HTMLElement)) return;
      wrapper.classList.remove('wawa-dropdown__cta');
      wrapper.style.removeProperty('grid-column');
      wrapper.style.removeProperty('grid-row');
    });

    const ctaWrapper = wrappers.find((wrapper) => {
      const directLink = wrapper.querySelector(':scope > .mega-menu__link');
      return directLink?.textContent?.trim().toLocaleLowerCase().startsWith('ver todo');
    });

    const categoryWrappers = ctaWrapper ? wrappers.filter((wrapper) => wrapper !== ctaWrapper) : wrappers;
    const rows = Math.ceil(categoryWrappers.length / 2);

    categoryWrappers.forEach((wrapper, index) => {
      if (!(wrapper instanceof HTMLElement)) return;
      const isLeftColumn = index < rows;
      const columnNumber = isLeftColumn ? 1 : 2;
      const rowNumber = isLeftColumn ? index + 1 : index - rows + 1;
      wrapper.style.gridColumn = String(columnNumber);
      wrapper.style.gridRow = String(rowNumber);
    });

    if (ctaWrapper instanceof HTMLElement) {
      ctaWrapper.classList.add('wawa-dropdown__cta');
      ctaWrapper.style.gridColumn = '1 / -1';
      ctaWrapper.style.gridRow = String(rows + 1);
    }

    this.#alignSubmenuToItem(item, submenu);
  }

  #alignSubmenuToItem(item, submenu) {
    const anchor = item.querySelector('.wawa-menu-title') ?? item;
    const inner = submenu.querySelector('.menu-list__submenu-inner');
    const panel = submenu.querySelector('.wawa-dropdown__panel, .mega-menu');
    if (!(anchor instanceof HTMLElement) || !(inner instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

    inner.style.removeProperty('padding-inline-start');
    requestAnimationFrame(() => {
      const anchorRect = anchor.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const currentPadding = Number.parseFloat(getComputedStyle(inner).paddingInlineStart) || 0;
      const viewportPadding = 16;
      const preferredPanelLeft = anchorRect.left + 4;
      const maxPanelLeft = Math.max(viewportPadding, window.innerWidth - panelRect.width - viewportPadding);
      const targetPanelLeft = Math.min(Math.max(viewportPadding, preferredPanelLeft), maxPanelLeft);
      const desiredPadding = Math.max(0, currentPadding + targetPanelLeft - panelRect.left);
      inner.style.paddingInlineStart = `${desiredPadding}px`;
    });
  }

  #ensureWawaDropdownStyles() {
    if (document.getElementById('wawa-premium-dropdown-styles')) return;

    const style = document.createElement('style');
    style.id = 'wawa-premium-dropdown-styles';
    style.textContent = `
      @media screen and (min-width: 990px) {
        .menu-list__submenu-inner.wawa-dropdown {
          justify-content: flex-start;
          flex-wrap: nowrap;
          padding-block-start: 12px;
          padding-block-end: 18px;
          overflow-x: visible;
        }

        .wawa-dropdown .wawa-dropdown__panel {
          display: block;
          width: min(380px, calc(100vw - 32px));
          max-width: 380px;
          margin: 0;
          padding: 14px 16px 12px;
          box-sizing: border-box;
          background: #DDE0DE;
          border: 1px solid #dde3da;
          border-radius: 12px;
          box-shadow: 0 14px 34px rgba(45, 52, 44, 0.10);
          overflow: hidden;
        }

        .wawa-dropdown .mega-menu__grid,
        .wawa-dropdown .mega-menu__list {
          display: block;
          width: 100%;
        }

        .wawa-dropdown .mega-menu__column.wawa-dropdown__grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          grid-auto-rows: minmax(38px, auto);
          column-gap: 14px;
          row-gap: 2px;
          width: 100%;
        }

        .wawa-dropdown .mega-menu__link {
          display: flex;
          align-items: center;
          width: 100%;
          min-height: 38px;
          padding: 8px 10px;
          box-sizing: border-box;
          border-radius: 7px;
          font-size: 15px;
          line-height: 1.35;
          font-weight: 500;
          color: #454845;
          transition: background-color 180ms ease, color 180ms ease, transform 180ms ease;
        }

        .wawa-dropdown .mega-menu__link:hover,
        .wawa-dropdown .mega-menu__link:focus-visible {
          background: #FFFFFF;
          color: #30352f;
          transform: translateX(2px);
        }

        .wawa-dropdown .mega-menu__link:focus-visible {
          outline: 1px solid #9aa695;
          outline-offset: 2px;
        }

        .wawa-dropdown .wawa-dropdown__cta {
          margin-top: 8px;
          padding-top: 9px;
          border-top: 1px solid #d9dfd6;
        }

        .wawa-dropdown .wawa-dropdown__cta .mega-menu__link {
          min-height: 34px;
          padding: 7px 4px;
          border-radius: 0;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #343934;
        }

        .wawa-dropdown .wawa-dropdown__cta .mega-menu__link:hover,
        .wawa-dropdown .wawa-dropdown__cta .mega-menu__link:focus-visible {
          background: transparent;
          color: #1f241f;
          transform: translateX(3px);
        }
      }
    `;

    document.head.appendChild(style);
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) cb(submenu);
      });
    };

    mapSubmenus((submenu) => submenu.style.setProperty('display', 'none'));
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => submenu.style.removeProperty('display'));
    return height;
  }

  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;
    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;
    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);
    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }
  return element?.querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
